import moment from 'moment'
import type { $Values } from 'utility-types'

import i18n from 'modules/core/lib/i18n'

const UNIT = {
  minutes: 'minutes',
  hours: 'hours',
}

function convertValueToMinutes(value: number, unit: $Values<typeof UNIT>) {
  if (unit === UNIT.hours) return Number(value) * 60

  return value
}

export function durationToText(
  value: number | string,
  unit: $Values<typeof UNIT>,
  alwaysRemainder = false,
  withPlusSign = false,
  onlyMinutes = false,
  useColumnSeparator = false
) {
  if (isNaN(Number(value))) return '-'

  const valueInMinutes: number = Math.floor(convertValueToMinutes(Number(value), unit))
  const quotient = Math.trunc(valueInMinutes / 60)
  const remainder = Math.abs(valueInMinutes % 60)

  let sign = ''

  // Add explicit + sign if required
  if (valueInMinutes > 0 && withPlusSign) {
    sign = '+' // Add - sign if raw value is negative and quotient is 0 (without this, it would show a string with a positive number)
  } else if (valueInMinutes < 0 && quotient === 0) {
    sign = '-'
  }

  if (useColumnSeparator) {
    return i18n.t('time_tracking.table.hours.digits', {
      hours: String(quotient).padStart(2, '0'),
      minutes: String(Math.floor(remainder)).padStart(2, '0'),
    })
  }

  if (!remainder && !alwaysRemainder) {
    return sign.concat(
      i18n.t('time_tracking.table.hours.precise', {
        hours: String(quotient),
      })
    )
  }

  const absValueInMinutes = Math.abs(valueInMinutes)

  if (absValueInMinutes < 60 && onlyMinutes) {
    const roundMinutes = Math.round(remainder * 100) / 100

    return sign.concat(
      i18n.t('time_tracking.table.minutes.precise', {
        minutes: String(roundMinutes),
      })
    )
  }

  return sign.concat(
    i18n.t('time_tracking.table.hours.remainder', {
      hours: String(quotient),
      minutes: String(Math.floor(remainder)).padStart(2, '0'),
    })
  )
}

export function minutesToText(
  value: number | string,
  alwaysRemainder = false,
  withPlusSign = false,
  onlyMinutes = false,
  useColumnSeparator = false
) {
  return durationToText(
    value,
    UNIT.minutes,
    alwaysRemainder,
    withPlusSign,
    onlyMinutes,
    useColumnSeparator
  )
}

export function hoursToText(value: number | string, alwaysRemainder = false, withPlusSign = false) {
  return durationToText(value, UNIT.hours, alwaysRemainder, withPlusSign)
}

export function durationInSeconds(startTime: string, endTime: string, isTimeFormat24H: boolean) {
  const formatTime = isTimeFormat24H ? 'HH:mm' : 'hh:mm a'
  const momentStartTime = moment(startTime, formatTime)
  const momentEndTime = moment(endTime, formatTime)

  // NOTE: night shifts conditional
  if (momentStartTime.isAfter(momentEndTime)) {
    const beginningOfDay = momentEndTime.clone().startOf('day')

    const totalSecondsFromEndTime = moment
      .duration(momentEndTime.diff(beginningOfDay))
      .as('seconds')

    const endOfDay = momentStartTime.clone().endOf('day')

    const totalSecondsFromEndOfStarttime = moment
      .duration(endOfDay.diff(momentStartTime))
      .as('seconds')

    return Math.round(totalSecondsFromEndTime + totalSecondsFromEndOfStarttime)
  }

  return moment.duration(momentEndTime.diff(momentStartTime)).as('seconds')
}
